import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from '@ovComponents/2-component/dialog/dialog';
import { Box, Button, Typography, useTheme } from 'hephaestus/web';
import { useFormatMoney } from '../hooks/useFormatMoney';
import {
  BLOTTER_BADGE_SX,
  BOLD_SX,
  LABEL_UPPER_SX,
  MICRO_SX,
  SECTION_HEADER_SX,
  TABULAR_SX,
  VALUE_SX,
} from '../styles';
import type { SysColors } from '../utils';
import { valueColor } from '../utils';
export type TradePreviewGroup = {
  saId: string;
  label: string;
  accountType: string;
  currency: string;
  trades: Array<{
    side: 'BUY' | 'SELL';
    ticker: string;
    tradeAmountCents: number;
  }>;
};

export type TradePreviewData = {
  groups: TradePreviewGroup[];
  totalBuyCents: number;
  totalSellCents: number;
  totalTrades: number;
  netCents: number;
};

type TradePreviewDialogProps = {
  open: boolean;
  onClose: () => void;
  previewData: TradePreviewData | null;
  onConfirm: () => void;
};

export default function TradePreviewDialog({
  open,
  onClose,
  previewData,
  onConfirm,
}: TradePreviewDialogProps) {
  const formatMoney = useFormatMoney();
  const theme = useTheme();
  const c = theme.color as SysColors;

  return (
    <Dialog
      open={open && !!previewData}
      onClose={onClose}
      maxWidth={false}
      // ovComponents Dialog does not expose paperProps — MUI class selector required for sizing
      sx={{ '.MuiDialog-paper': { width: 440, maxHeight: '80vh' } }}
    >
      {previewData && (
        <>
          <DialogTitle onClose={onClose}>
            <Box>
              <Typography
                variant='bodyMedium'
                sx={{ fontWeight: 600, mb: 0.25 }}
              >
                Generate {previewData.totalTrades} trade
                {previewData.totalTrades !== 1 ? 's' : ''}?
              </Typography>
              <Typography
                variant='labelSmall'
                sx={{ color: theme.color.onSurfaceVariant, lineHeight: 1.4 }}
              >
                Requests will be created in <strong>INITIATED</strong> state for
                verification.
              </Typography>
            </Box>
          </DialogTitle>

          <Box
            sx={{
              display: 'flex',
              borderBottom: '1px solid',
              borderColor: theme.color.outlineVariant,
              bgcolor: theme.color.background,
            }}
          >
            {[
              {
                label: 'BUYS',
                val: formatMoney(previewData.totalBuyCents),
                color: theme.color.positive,
                count: previewData.groups.reduce(
                  (n, g) => n + g.trades.filter((t) => t.side === 'BUY').length,
                  0,
                ),
              },
              {
                label: 'SELLS',
                val: formatMoney(previewData.totalSellCents),
                color: theme.color.negative,
                count: previewData.groups.reduce(
                  (n, g) =>
                    n + g.trades.filter((t) => t.side === 'SELL').length,
                  0,
                ),
              },
              {
                label: 'NET',
                val:
                  previewData.netCents > 0
                    ? `${formatMoney(previewData.netCents)} buy`
                    : previewData.netCents < 0
                      ? `${formatMoney(Math.abs(previewData.netCents))} sell`
                      : '$0',
                color: valueColor(c, previewData.netCents),
                count: undefined as number | undefined,
              },
            ].map((m, i, arr) => (
              <Box
                key={m.label}
                sx={{
                  flex: 1,
                  py: 0.75,
                  px: 1.5,
                  textAlign: 'center',
                  borderRight: i < arr.length - 1 ? '1px solid' : 'none',
                  borderColor: theme.color.outlineVariant,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.5,
                  }}
                >
                  <Typography
                    variant='labelSmall'
                    sx={{ ...SECTION_HEADER_SX, ...MICRO_SX, lineHeight: 1 }}
                  >
                    {m.label}
                  </Typography>
                  {m.count !== undefined && m.count > 0 && (
                    <Typography
                      variant='labelSmall'
                      sx={{
                        ...MICRO_SX,
                        ...BOLD_SX,
                        color: m.color,
                        lineHeight: 1,
                      }}
                    >
                      ({m.count})
                    </Typography>
                  )}
                </Box>
                <Typography
                  variant='bodySmall'
                  sx={{
                    fontWeight: 600,
                    ...TABULAR_SX,
                    color: m.color,
                    lineHeight: 1.3,
                  }}
                >
                  {m.val}
                </Typography>
              </Box>
            ))}
          </Box>

          <DialogContent sx={{ p: '0 !important' }}>
            <Box sx={{ px: 2, py: 1 }}>
              {previewData.groups.map((group) => (
                <Box key={group.saId} sx={{ mb: 1.5 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      mb: 0.25,
                    }}
                  >
                    <Typography
                      variant='labelSmall'
                      sx={{
                        ...LABEL_UPPER_SX,
                        fontWeight: 800,
                        color: theme.color.onSurfaceVariant,
                      }}
                    >
                      {group.label}
                    </Typography>
                    {group.accountType && (
                      <Typography
                        variant='labelSmall'
                        sx={{ ...MICRO_SX, color: theme.color.onDisabled }}
                      >
                        {group.accountType}
                      </Typography>
                    )}
                    <Typography
                      variant='labelSmall'
                      sx={{
                        ...MICRO_SX,
                        color: theme.color.onDisabled,
                        ml: 'auto',
                      }}
                    >
                      {group.currency}
                    </Typography>
                  </Box>

                  {group.trades.map((t, i) => (
                    <Box
                      key={`${t.ticker}-${i}`}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        py: 0.25,
                        px: 0.5,
                        borderBottom: '1px solid',
                        borderColor: theme.color.outlineVariant,
                        '&:last-child': { borderBottom: 'none' },
                        borderLeft: '2px solid',

                        borderLeftColor:
                          t.side === 'BUY'
                            ? theme.color.positive
                            : theme.color.negative,

                        transition: 'background 0.1s ease',
                        '&:hover': { bgcolor: theme.color.neutralHover },
                      }}
                    >
                      <Typography
                        variant='labelSmall'
                        sx={{ ...VALUE_SX, flex: 1 }}
                      >
                        {t.ticker}
                      </Typography>
                      <Box
                        sx={{
                          ...BLOTTER_BADGE_SX,
                          bgcolor:
                            t.side === 'BUY'
                              ? theme.color.positiveVariant
                              : theme.color.negativeVariant,
                          mr: 1,
                        }}
                      >
                        <Typography
                          variant='labelSmall'
                          sx={{
                            ...MICRO_SX,
                            fontWeight: 800,
                            color:
                              t.side === 'BUY'
                                ? theme.color.positive
                                : theme.color.negative,
                          }}
                        >
                          {t.side}
                        </Typography>
                      </Box>
                      <Typography
                        variant='labelSmall'
                        sx={{
                          ...VALUE_SX,
                          textAlign: 'right',
                          fontWeight: 700,
                          color:
                            t.side === 'BUY'
                              ? theme.color.positive
                              : theme.color.negative,
                          minWidth: 70,
                        }}
                      >
                        {formatMoney(Math.abs(t.tradeAmountCents))}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ))}
            </Box>
          </DialogContent>

          <DialogFooter>
            <Button variant='text' size='small' onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant='contained'
              size='small'
              onClick={() => {
                onConfirm();
                onClose();
              }}
            >
              Confirm
            </Button>
          </DialogFooter>
        </>
      )}
    </Dialog>
  );
}
